import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useState } from "react"
import { useCreateCondominium } from "@/hooks/useCondominiums"
import { toast } from "sonner"
import { ORG_TYPE_PRESET, type AppModule, type OrgType } from "@/lib/modules"
import { ModulePicker } from "./ModulePicker"

interface CreateCondominiumModalProps {
  isOpen: boolean
  onClose: () => void
}

const typeLabels: Record<OrgType, string> = {
  condominio: "Condomínio",
  ponto_eletronico: "Ponto Eletrônico",
  controle_acessos: "Controle de Acessos"
}

const emptyForm = () => ({
  name: "",
  address: "",
  subscription_months: 12,
  type: "condominio" as OrgType,
  enabled_modules: ORG_TYPE_PRESET.condominio as AppModule[]
})

const CreateCondominiumModal = ({ isOpen, onClose }: CreateCondominiumModalProps) => {
  const createCondominiumMutation = useCreateCondominium()
  const [newCondominium, setNewCondominium] = useState(emptyForm)

  const handleCreate = () => {
    if (!newCondominium.name || !newCondominium.address) {
      toast.error("Preencha todos os campos obrigatórios")
      return
    }

    createCondominiumMutation.mutate(newCondominium, {
      onSuccess: () => {
        setNewCondominium(emptyForm())
        onClose()
      }
    })
  }

  const label = typeLabels[newCondominium.type]

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Criar Nova Organização</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="type">Tipo</Label>
            <Select
              value={newCondominium.type}
              onValueChange={(value: OrgType) => setNewCondominium({...newCondominium, type: value, enabled_modules: ORG_TYPE_PRESET[value]})}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="condominio">Condomínio</SelectItem>
                <SelectItem value="ponto_eletronico">Ponto Eletrônico</SelectItem>
                <SelectItem value="controle_acessos">Controle de Acessos</SelectItem>
              </SelectContent>
            </Select>
            <p className="mt-1 text-[12px] text-muted-foreground">O tipo só sugere os módulos; ajuste abaixo.</p>
          </div>
          <div>
            <Label>Módulos</Label>
            <div className="mt-1.5">
              <ModulePicker
                value={newCondominium.enabled_modules}
                onChange={(enabled_modules) => setNewCondominium({...newCondominium, enabled_modules})}
              />
            </div>
          </div>
          <div>
            <Label htmlFor="name">Nome da Organização</Label>
            <Input
              id="name"
              value={newCondominium.name}
              onChange={(e) => setNewCondominium({...newCondominium, name: e.target.value})}
              placeholder={`Nome do ${label.toLowerCase()}`}
            />
          </div>
          <div>
            <Label htmlFor="address">Endereço</Label>
            <Input
              id="address"
              value={newCondominium.address}
              onChange={(e) => setNewCondominium({...newCondominium, address: e.target.value})}
              placeholder="Endereço completo"
            />
          </div>
          <div>
            <Label htmlFor="months">Duração da Assinatura (meses)</Label>
            <Select value={newCondominium.subscription_months.toString()} onValueChange={(value) => setNewCondominium({...newCondominium, subscription_months: parseInt(value)})}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1 mês</SelectItem>
                <SelectItem value="3">3 meses</SelectItem>
                <SelectItem value="6">6 meses</SelectItem>
                <SelectItem value="12">12 meses</SelectItem>
                <SelectItem value="24">24 meses</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            onClick={handleCreate}
            className="w-full"
            disabled={createCondominiumMutation.isPending}
          >
            {createCondominiumMutation.isPending ? "Criando..." : "Criar Organização"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export { CreateCondominiumModal }
